/**
 * Preferências de interface no servidor (spec 017, FR-006): layout, densidade e
 * barra recolhida seguem a pessoa entre navegadores — até aqui eram só
 * localStorage. Vêm no /me (`uiPrefs` + o `defaultLayout` do tenant) e voltam
 * por PUT /me/ui-prefs, que SUBSTITUI o objeto: por isso a escrita junta o que
 * já está salvo com a mudança, e só depois de o /me ter chegado — mandar só a
 * mudança antes disso apagaria as outras preferências.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { resolveLayout, type Profile, type UiLayout, type UiPrefs } from '@erp/shared';
import { api } from '@/lib/api-client';
import { profileQueryOptions } from '@/features/account/account-queries';

/**
 * O layout Simplificado existe (spec 018): cada pessoa escolhe no menu do
 * usuário, e a escolha segue a pessoa (017, FR-006).
 */
export const SIMPLIFIED_LAYOUT_AVAILABLE = true;

/**
 * Spec 018, FR-007: o Simplificado é OPT-IN POR PESSOA até o piloto aprovar —
 * o admin ainda não pode torná-lo o padrão do tenant (o que mudaria a tela de
 * todo mundo de uma vez). Quando o piloto aprovar, isto vira `true`.
 */
export const SIMPLIFIED_LAYOUT_AS_TENANT_DEFAULT = false;

export interface UiPrefsState {
  /** undefined enquanto o /me não chegou */
  prefs: UiPrefs | undefined;
  /** layout padrão do tenant (org_settings.default_layout) */
  defaultLayout: UiLayout;
  /** a escolha da pessoa, ou o padrão do tenant */
  layout: UiLayout;
  ready: boolean;
  /** grava a mudança por cima do que está salvo (no-op antes do /me) */
  update: (patch: UiPrefs) => void;
}

export function useUiPrefs(): UiPrefsState {
  const qc = useQueryClient();
  const profileQ = useQuery({ ...profileQueryOptions(), staleTime: 60_000 });
  const key = profileQueryOptions().queryKey;
  const prefs = profileQ.data?.uiPrefs;
  const defaultLayout = profileQ.data?.defaultLayout ?? 'classic';

  const save = useMutation({
    mutationFn: async (next: UiPrefs) => {
      const res = await api<UiPrefs>('/me/ui-prefs', { method: 'PUT', body: next });
      if (res.error) throw res.error;
      return res.data;
    },
    // otimista: a barra recolhe na hora; erro devolve o que o servidor tem
    onMutate: (next) => {
      qc.setQueryData<Profile>(key, (p) => (p ? { ...p, uiPrefs: next } : p));
    },
    onError: () => void qc.invalidateQueries({ queryKey: key }),
  });

  return {
    prefs,
    defaultLayout,
    layout: resolveLayout(prefs, defaultLayout),
    ready: prefs !== undefined,
    update: (patch) => {
      if (prefs === undefined) return;
      save.mutate({ ...prefs, ...patch });
    },
  };
}
